// Usage: calculateFreight(postcode, length, width, height, weight, suburb)
// Data file: freightData.json
// Fuel levy from: ../data/levy.json (dfe key)

import freightData from './freightData.json';
import levy from '../data/levy.json';

// ========== CONSTANTS ==========
const FUEL_LEVY_RATE = levy.dfe; // DFE 燃油费率 from levy.json (34.2%)
const CUBIC_CONVERSION = 4000; // cm³ to kg - 匹配输出表格 B5=MAX(L*W*H/4000, weight)

// ========== SIZE/WEIGHT LIMITS (from Excel formula) ==========
const MAX_WEIGHT = 30;           // kg
const MAX_SINGLE_DIMENSION = 120; // cm - 单边不超过 120cm
const MAX_TWO_SIDES = 70;        // cm - 不能有两边同时超过 70cm

// ========== HELPERS ==========

function getZone(postcode, zonesMap) {
  const pc = String(postcode).trim().padStart(4, '0');
  return zonesMap[pc] || zonesMap[String(parseInt(pc, 10))] || null;
}

function getFreightRate(zone, suburb, rates) {
  if (!zone) return null;
  const zu = zone.toUpperCase().trim();
  const su = suburb ? suburb.toUpperCase().trim() : '';

  if (su) {
    const match = rates.find(
      (r) => r.to.toUpperCase() === zu && r.suburb && r.suburb.toUpperCase() === su
    );
    if (match) return match;
  }

  return rates.find((r) => r.to.toUpperCase() === zu && !r.suburb) || null;
}

function getRemoteSurcharge(postcode, remoteMap) {
  if (!postcode) return 0;
  const pc = String(postcode).trim();
  // Simple postcode lookup - if postcode exists in map, return surcharge
  const surcharge = remoteMap[pc] || remoteMap[pc.padStart(4, '0')];
  return surcharge !== undefined ? surcharge : 0;
}

/**
 * 验证尺寸和重量限制 (匹配 Excel 公式)
 * IF(OR(weight>30, MAX(dims)>120, AND(MAX(dims)>70, LARGE(dims,2)>70)), "", ...)
 * @returns {string|null} 错误信息或 null (通过验证)
 */
function validateDimensionsAndWeight(weight, length, width, height) {
  if (weight > MAX_WEIGHT) {
    return `Weight ${weight}kg exceeds maximum ${MAX_WEIGHT}kg`;
  }

  const dims = [length, width, height];
  const maxDim = Math.max(...dims);

  if (maxDim > MAX_SINGLE_DIMENSION) {
    return `Max dimension ${maxDim}cm exceeds ${MAX_SINGLE_DIMENSION}cm limit`;
  }

  // 两边都超过70cm也不行: AND(MAX(dims)>70, LARGE(dims,2)>70)
  const sorted = [...dims].sort((a, b) => b - a); // descending
  if (sorted[0] > MAX_TWO_SIDES && sorted[1] > MAX_TWO_SIDES) {
    return `Two dimensions exceed ${MAX_TWO_SIDES}cm (${sorted[0]}cm, ${sorted[1]}cm)`;
  }

  return null;
}

// ========== CALCULATION ==========

function calculateFreight(postcode, length, width, height, weight, suburb = '') {
  const { zones, rates, remoteList } = freightData;

  const result = {
    postcode: String(postcode).trim(),
    zone: null,
    suburb: suburb ? suburb.toUpperCase().trim() : '',
    cubicWeight: 0,
    chargeableWeight: 0,
    basicCharge: 0,
    ratePerKg: 0,
    minCharge: 0,
    calculatedCost: 0,
    fuelLevy: 0,
    remoteSurcharge: 0,
    totalFreight: 0,
    isSuburbRate: false,
    error: null,
  };

  // 1. Parse dimensions and weight
  const l = parseFloat(length) || 0;
  const w = parseFloat(width) || 0;
  const h = parseFloat(height) || 0;
  const aw = parseFloat(weight) || 0;

  // 3. Chargeable weight: CEILING(MAX(actual, cubic), 1) — 向上取整
  // NOTE: Must calculate before validation because Excel uses chargeable weight (B5) for limits
  const cubicWeight = (l * w * h) / CUBIC_CONVERSION;
  const chargeableWeight = Math.ceil(Math.max(aw, cubicWeight));

  // 2. 验证尺寸和重量限制 (Excel: IF(OR(B5>30, MAX(dims)>120, AND(MAX>70, 2nd>70)), "", ...))
  //    B5 = 计费重量 (chargeableWeight)
  const validationError = validateDimensionsAndWeight(chargeableWeight, l, w, h);
  if (validationError) {
    result.error = validationError;
    return result;
  }

  // 3. Postcode -> Zone
  result.zone = getZone(postcode, zones);
  if (!result.zone) {
    result.error = `No zone found for postcode ${postcode}`;
    return result;
  }

  result.cubicWeight = +cubicWeight.toFixed(5);
  result.chargeableWeight = chargeableWeight;

  // 4. Zone + Suburb -> Rate
  const rate = getFreightRate(result.zone, suburb, rates);
  if (!rate) {
    result.error = `No rate found for zone ${result.zone}${suburb ? ` / suburb ${suburb}` : ''}`;
    return result;
  }
  result.basicCharge = +rate.basicCharge.toFixed(5);
  result.ratePerKg = +rate.rate.toFixed(5);
  result.minCharge = +rate.minCharge.toFixed(5);
  result.isSuburbRate = !!rate.suburb;

  // 5. Excel 公式: IF(E+(CEILING(weight,1)*F) > G, (E+CEIL*F)*(1+fuel), G*(1+fuel))
  //    即: MAX(basicCharge + ceil(weight)*rate, minCharge) * (1 + fuelRate)
  const weightBased = rate.basicCharge + chargeableWeight * rate.rate;
  const calculatedCost = Math.max(weightBased, rate.minCharge);
  result.calculatedCost = +calculatedCost.toFixed(5);

  // 6. 燃油附加费: calculatedCost * (1 + fuelRate)
  const costWithFuel = calculatedCost * (1 + FUEL_LEVY_RATE);
  result.fuelLevy = +(costWithFuel - calculatedCost).toFixed(5);

  // 7. Remote surcharge: IFERROR(VLOOKUP(postcode, remote!B:C, 2, 0), 0)
  const remoteSurcharge = getRemoteSurcharge(postcode, remoteList);
  result.remoteSurcharge = +remoteSurcharge.toFixed(5);

  // 8. Total = costWithFuel + remoteSurcharge
  const totalFreight = costWithFuel + remoteSurcharge;
  result.totalFreight = +totalFreight.toFixed(5);

  return result;
}

// 适配器函数，匹配其他快递公司的接口: (postcode, weight, length)
// 假设默认宽度和高度为 30cm
export const calculateShipping = (postcode, weight, length, width = 30, height = 30, suburb = '') => {
  const result = calculateFreight(postcode, length, width, height, weight, suburb);

  // 如果有错误，返回一个很大的值
  if (result.error) {
    return 999999;
  }

  return result.totalFreight;
};

// 导出详细计算函数和辅助函数供需要的地方使用
export { calculateFreight, getZone, getFreightRate, getRemoteSurcharge };

/*
使用说明
1. calculateShipping(postcode, weight, length, [width], [height], [suburb]) - 简化接口，返回运费总额
2. calculateFreight(postcode, length, width, height, weight, [suburb]) - 详细接口，返回完整计算信息
3. 参数说明:
   - postcode: 邮编 (必填)
   - length: 长度 (cm)
   - width: 宽度 (cm)
   - height: 高度 (cm)
   - weight: 实际重量 (kg)
   - suburb: 郊区名称 (可选，某些特殊区域需要)
4. 尺寸/重量限制 (超出返回错误):
   - 重量 ≤ 30kg
   - 最长边 ≤ 120cm
   - 不允许两边同时 > 70cm
5. 燃油附加费率: 从 levy.json 的 dfe 字段读取 (34.2%)
6. 立方重量计算: (长 × 宽 × 高) ÷ 4000
7. 计费重量: CEILING(MAX(实际重量, 立方重量), 1) — 向上取整
8. 运费公式: MAX(基础运费 + 费率 × 计费重量, 最低收费) × (1 + 燃油费率) + 偏远附加费
9. 偏远附加费: VLOOKUP(postcode, remote表, 2, 0)，查不到则为 0
10. 所有计算结果保留 5 位小数
*/
